import React, { useRef, useState, useEffect, FC } from 'react';
import styled from 'styled-components';
import { Icons } from '../../../../../assets';
import { IRestaraunt } from '../../../../../entities/rKeeper';
import { colors } from '../../../../../styles';
import { flexMixin, textMixin } from '../../../../../styles/mixins';

interface IProps {
    options: IRestaraunt[];
    setSelectValue: (value: IRestaraunt[]) => void;
    value: IRestaraunt[];
}

export const RevenueSelector: FC<IProps> = ({
    options,
    setSelectValue,
    value,
}) => {
    const [switchIsOpen, setSwitchIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const isAllSelected = value.length === options.length;

    const selectRestaurant = (restaurant: IRestaraunt) => {
        if (value.length === 1 && value[0].id === restaurant.id) {
            setSelectValue([]);
        } else {
            setSelectValue([restaurant]);
        }
        setSwitchIsOpen(false);
    };

    const selectAllRestaurants = () => {
        if (isAllSelected) {
            setSelectValue([]);
        } else {
            setSelectValue([...options]);
        }
        setSwitchIsOpen(false);
    };

    const getDisplayText = () => {
        if (isAllSelected) return 'Все рестораны';
        if (value.length === 1) return value[0].name;
        return 'Выберите ресторан';
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setSwitchIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () =>
            document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <SelectorWrap $isOpen={switchIsOpen}>
            <SelectWrapper onClick={() => setSwitchIsOpen(!switchIsOpen)}>
                <Title>{getDisplayText()}</Title>
                <NoiseSelectorBtn>
                    {switchIsOpen ? (
                        <img src={Icons.IconArrowUp} alt="Proga.pro arrow" />
                    ) : (
                        <img
                            src={Icons.IconArrowDown}
                            alt="Proga.pro arrow down"
                        />
                    )}
                </NoiseSelectorBtn>
            </SelectWrapper>

            {switchIsOpen && (
                <SelectorDropdown $isOpen={switchIsOpen} ref={dropdownRef}>
                    <TitleLabel
                        onClick={selectAllRestaurants}
                        $isSelected={isAllSelected}
                    >
                        <span>Все рестораны</span>
                    </TitleLabel>

                    {options.map(item => (
                        <TitleLabel
                            key={item.id}
                            onClick={() => selectRestaurant(item)}
                            $isSelected={
                                !isAllSelected &&
                                value.some(v => v.id === item.id)
                            }
                        >
                            <span>{item.name}</span>
                        </TitleLabel>
                    ))}
                </SelectorDropdown>
            )}
        </SelectorWrap>
    );
};

const SelectorWrap = styled.div<{ $isOpen: boolean }>`
    width: 100%;
    ${flexMixin('column', 'center', 'flex-start')};
    background: ${colors.cleanWhite};
    border-radius: 8px 8px 0px 0px;
    position: relative;
    line-height: 26px;
    ${textMixin({ fontSize: '13px', fontWeight: 600 })};
    color: #1f1e24;
    cursor: pointer;
`;

const Title = styled.div`
    width: 100%;
    padding: 6px 0px 0px 16px;
    ${textMixin({ fontSize: '30px', fontWeight: 600 })};
    height: 100%;
    line-height: 34px;
    ${flexMixin('column', 'flex-start', 'flex-start')};
    text-align: center;
    color: ${colors.grey};

    @media (max-width: 1500px) {
        font-size: 24px;
        padding: 6px 0px 0px 16px;
    }
`;

const TitleLabel = styled.div<{ $isSelected?: boolean }>`
    ${textMixin({ fontSize: '30px', fontWeight: 600 })};
    line-height: 34px;
    color: ${({ $isSelected }) =>
        $isSelected ? colors.primaryBg : colors.grey};
    width: 100%;
    height: 38px;
    ${flexMixin('column', 'center', 'flex-start')};
    text-align: center;

    & span {
        ${textMixin({ fontSize: '14px', fontWeight: 500 })};
        line-height: 28px;
        color: ${({ $isSelected }) =>
            $isSelected ? colors.primaryBg : colors.grey};
    }

    &:hover {
        background-color: ${colors.lightGrey};
    }
`;

const SelectorDropdown = styled.div<{ $isOpen: boolean }>`
    width: 100%;
    position: absolute;
    ${flexMixin('column', 'center', 'center')};
    z-index: 8;
    padding: 0px 26px 6px 26px;
    top: 62px;
    background-color: ${colors.cleanWhite};
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    border-top-left-radius: ${({ $isOpen }) => ($isOpen ? '0px' : '8px')};
    border-top-right-radius: ${({ $isOpen }) => ($isOpen ? '0px' : '8px')};
    box-shadow: ${({ $isOpen }) =>
        $isOpen
            ? '0px 8px 8px 0px rgb(158 158 158 / 40%)'
            : '0px 0px 8px 0px rgb(158 158 158 / 40%)'};

    ::-webkit-scrollbar {
        width: 14px;
        background-color: inherit;
    }

    ::-webkit-scrollbar-thumb {
        height: 140px;
        background-color: #e7e7e7;
    }
`;

const NoiseSelectorBtn = styled.div`
    position: absolute;
    right: 6px;
    width: 30px;
    padding-top: 10px;
    height: 100%;
    ${flexMixin('column', 'center', 'center')};

    & svg path {
        fill: #a7a7a7;
    }
`;

const SelectWrapper = styled.div`
    height: 100%;
    ${flexMixin('column', 'center', 'center')};
`;
